class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.onVehicleRegistrationInput = this.onVehicleRegistrationInput.bind(this);
    this.onVehicleRegistrationClick = this.onVehicleRegistrationClick.bind(this);
    this.onVehicleRegistrationSubmit = this.onVehicleRegistrationSubmit.bind(this);

    this.initVehicleRegistrationGuard();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  initVehicleRegistrationGuard() {
    if (this.vehicleRegistrationGuardInitialised) return;

    this.addEventListener('input', this.onVehicleRegistrationInput);
    this.addEventListener('click', this.onVehicleRegistrationClick);
    this.addEventListener('submit', this.onVehicleRegistrationSubmit, true);

    this.vehicleRegistrationGuardInitialised = true;
  }

  getVehicleRegistrationElements() {
    return {
      form: this.querySelector('#CartDrawer-Form'),
      checkoutButton: this.querySelector('#CartDrawer-Checkout'),
      registrationInput: this.querySelector('#CartDrawer-VehicleRegistration'),
      warning: this.querySelector('#CartDrawer-VehicleRegistrationWarning'),
    };
  }

  requiresVehicleRegistration() {
    const { checkoutButton } = this.getVehicleRegistrationElements();
    return checkoutButton && checkoutButton.dataset.requiresVehicleRegistration === 'true';
  }

  getVehicleRegistrationValue() {
    const { registrationInput } = this.getVehicleRegistrationElements();
    return registrationInput ? registrationInput.value.trim() : '';
  }

  showVehicleRegistrationWarning() {
    const { registrationInput, warning } = this.getVehicleRegistrationElements();
    if (!registrationInput || !warning) return;

    warning.hidden = false;
    registrationInput.classList.add('is-error');
  }

  hideVehicleRegistrationWarning() {
    const { registrationInput, warning } = this.getVehicleRegistrationElements();
    if (!registrationInput || !warning) return;

    warning.hidden = true;
    registrationInput.classList.remove('is-error');
  }

  getCartNoteElement() {
    return this.querySelector('#CartDrawer-Note') || this.querySelector('#CartDrawer-HiddenNote');
  }

  syncVehicleRegistrationToNote() {
    const noteField = this.getCartNoteElement();
    const registrationValue = this.getVehicleRegistrationValue();

    if (!noteField) return;

    const prefix = 'Vehicle Registration:';
    let noteValue = noteField.value || '';

    noteValue = noteValue
      .replace(/^Vehicle Registration:.*(\r?\n)?/gim, '')
      .replace(/^\s+|\s+$/g, '');

    if (registrationValue !== '') {
      noteValue = noteValue
        ? `${noteValue}\n${prefix} ${registrationValue}`
        : `${prefix} ${registrationValue}`;
    }

    noteField.value = noteValue;
  }

  updateVehicleRegistrationState() {
    const { checkoutButton, registrationInput } = this.getVehicleRegistrationElements();
    if (!checkoutButton || !registrationInput) return;

    this.syncVehicleRegistrationToNote();

    if (!this.requiresVehicleRegistration()) {
      checkoutButton.setAttribute('aria-disabled', 'false');
      checkoutButton.classList.remove('is-disabled');
      this.hideVehicleRegistrationWarning();
      return;
    }

    const hasValue = this.getVehicleRegistrationValue() !== '';

    checkoutButton.setAttribute('aria-disabled', hasValue ? 'false' : 'true');
    checkoutButton.classList.toggle('is-disabled', !hasValue);

    if (hasValue) {
      this.hideVehicleRegistrationWarning();
    }
  }

  onVehicleRegistrationInput(event) {
    if (event.target.id !== 'CartDrawer-VehicleRegistration') return;
    this.updateVehicleRegistrationState();
  }

  onVehicleRegistrationClick(event) {
    const checkoutButton = event.target.closest('#CartDrawer-Checkout');
    if (!checkoutButton) return;

    if (!this.requiresVehicleRegistration()) return;

    if (this.getVehicleRegistrationValue() === '') {
      event.preventDefault();
      event.stopPropagation();
      this.showVehicleRegistrationWarning();

      const { registrationInput } = this.getVehicleRegistrationElements();
      if (registrationInput) registrationInput.focus();
    }
  }

  onVehicleRegistrationSubmit(event) {
    const form = event.target;
    if (!form || form.id !== 'CartDrawer-Form') return;

    this.syncVehicleRegistrationToNote();

    if (!this.requiresVehicleRegistration()) return;

    if (this.getVehicleRegistrationValue() === '') {
      event.preventDefault();
      this.showVehicleRegistrationWarning();

      const { registrationInput } = this.getVehicleRegistrationElements();
      if (registrationInput) registrationInput.focus();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.updateVehicleRegistrationState();

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.updateVehicleRegistrationState();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);